import { HttpClient } from '@angular/common/http';
import { inject, signal, computed, Service } from '@angular/core';
import { Observable, catchError, map, switchMap, throwError } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';

import { environment } from '../../environments/environment';
import { LicenseActivationError } from './license-activation.error';
import { handleApiError } from '../../_utils/error-handling';

export interface License {
  code: string;
  methodName: string;
  productName: string;
  expiryDate: string;
  ssoUrl?: string | null;
}

// "Soon to expire" threshold used by both the entry-point routing and the
// /licenses page colour coding.
export const EXPIRY_SOON_MONTHS = 1;

@Service()
export class LicenseService {
  #http = inject(HttpClient);

  readonly licenses = signal<Array<License>>([]);

  readonly busy = signal(false);

  readonly activeLicenses = computed(() =>
    this.licenses().filter((l) => l.expiryDate > new Date().toISOString()),
  );

  // True when the user has at least one active license and none of them
  // expire within EXPIRY_SOON_MONTHS — the condition for sending the user
  // straight to the material instead of /licenses.
  readonly hasUsableActiveLicenses = computed(() => {
    const active = this.activeLicenses();
    if (active.length === 0) {
      return false;
    }
    const soon = new Date();
    soon.setMonth(soon.getMonth() + EXPIRY_SOON_MONTHS);
    return active.every((l) => new Date(l.expiryDate) >= soon);
  });

  // Target for "Naar de lessen": when every active license carries the same
  // non-null ssoUrl, use that; otherwise fall back to the env-level edition
  // URL. Inactive (expired) licenses are ignored.
  readonly materialUrl = computed(() => {
    const active = this.activeLicenses();
    const urls = new Set(active.map((l) => l.ssoUrl ?? null));
    if (urls.size === 1) {
      const onlyUrl = urls.values().next().value;
      if (onlyUrl) {
        return onlyUrl;
      }
    }
    return environment.editionFallbackUrl;
  });

  getLicenses(): Observable<Array<License>> {
    return this.#http.get<Array<License>>('/api/licenses').pipe(
      tap((licenses) => {
        this.licenses.set(licenses);
      }),
    );
  }

  // Wraps the window.location.replace call so callers (bootstrap initializer,
  // login callback) don't reach into globals — and so tests can mock it
  // without fighting jsdom's non-writable Location.prototype.replace.
  /* istanbul ignore next: thin wrapper around window.location.replace */
  redirectToMaterial(): void {
    window.location.replace(this.materialUrl());
  }

  activateLicense(activationCode: string): Observable<void> {
    if (!LicenseService.validateActivationCodeFormat(activationCode)) {
      return throwError(() => new LicenseActivationError('code_invalid'));
    }

    this.busy.set(true);
    return this.#http
      .post<void>('/api/licenses/activate', { activationCode })
      .pipe(
        switchMap(() => this.getLicenses().pipe(map(() => undefined))),
        catchError((error: unknown) => {
          return handleApiError(error).pipe(
            catchError((code) =>
              throwError(() => new LicenseActivationError(code)),
            ),
          );
        }),
        finalize(() => this.busy.set(false)),
      );
  }

  private static validateActivationCodeFormat(activationCode: string): boolean {
    const licensePattern = /^[0-9A-Z]{8}$/;
    return licensePattern.test(activationCode);
  }
}
